import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getPortalInfo, checkParticipantEmail, submitParticipantDocument } from '../services/api';

const DOC_TYPES = [
  { key: 'letter_to_parents', label: 'Letter to Parents', code: 'F8', hasTemplate: true },
  { key: 'risk_assessment', label: 'Individual Risk Assessment', code: 'F10', hasTemplate: true },
  { key: 'insurance', label: 'Insurance', code: null, hasTemplate: false },
  { key: 'moa', label: 'Memorandum of Agreement (MOA)', code: 'F11', hasTemplate: true },
  { key: 'medical_clearance', label: 'Medical Clearance (UHS)', code: null, hasTemplate: true },
];

// steps: 'email' → 'details' (new only) → 'documents' → 'success'

const UploadIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
  </svg>
);
const CheckIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
  </svg>
);
const DownloadIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const STEP_LABELS = {
  email: { title: 'Enter Your Email', step: '1 of 3' },
  details: { title: 'Participant Details', step: '2 of 3' },
  documents: { title: 'Required Documents', step: '3 of 3' },
};

const ParticipantPortal = () => {
  const { token } = useParams();

  // Portal meta
  const [portalInfo, setPortalInfo] = useState(null);
  const [portalError, setPortalError] = useState(null);
  const [portalLoading, setPortalLoading] = useState(true);

  // Steps
  const [step, setStep] = useState('email'); // 'email' | 'details' | 'documents' | 'success'

  // Email step
  const [emailInput, setEmailInput] = useState('');
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Whether this is a returning participant
  const [isReturning, setIsReturning] = useState(false);

  // Full identity (built up across steps)
  const [identity, setIdentity] = useState({ slsu_email: '', full_name: '', program: '', unit_dept_org: '' });
  const [detailsError, setDetailsError] = useState('');

  // Existing docs (for returning participants)
  const [existingDocs, setExistingDocs] = useState({});

  // Upload state
  const [uploadState, setUploadState] = useState({});
  const fileRefs = useRef({});

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getPortalInfo(token);
        setPortalInfo(res.data);
      } catch (err) {
        const msg = err.response?.data?.detail || 'This link is invalid or has expired.';
        setPortalError(msg);
      } finally {
        setPortalLoading(false);
      }
    };
    load();
  }, [token]);

  const formatDeadline = () => {
    if (!portalInfo?.lock_date) return null;
    try {
      const d = new Date(portalInfo.lock_date + 'T' + (portalInfo.lock_time || '23:59') + ':00');
      if (isNaN(d.getTime())) return null;
      return d.toLocaleString('en-PH', { month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return null;
    }
  };

  // ── Step 1: Email check ─────────────────────────────────────────────────────
  const handleEmailContinue = async (e) => {
    e.preventDefault();
    const email = emailInput.trim();
    if (!email) { setEmailError('Please enter your SLSU email.'); return; }
    if (!email.includes('@')) { setEmailError('Please enter a valid email address.'); return; }
    setEmailError('');
    setEmailChecking(true);
    try {
      const res = await checkParticipantEmail(token, email);
      if (res.data.exists) {
        // Returning participant — skip details form
        setIsReturning(true);
        setIdentity(prev => ({ ...prev, slsu_email: email }));
        const docs = res.data.documents || {};
        setExistingDocs(docs);
        const init = {};
        DOC_TYPES.forEach(dt => {
          if (docs[dt.key]) {
            init[dt.key] = { file: null, uploading: false, done: true, error: '' };
          }
        });
        setUploadState(init);
        // Jump straight to documents — no need to fill details again
        setStep('documents');
      } else {
        // New participant — collect details
        setIsReturning(false);
        setIdentity(prev => ({ ...prev, slsu_email: email }));
        setStep('details');
      }
    } catch {
      // Network error — still let them proceed as new participant
      setIdentity(prev => ({ ...prev, slsu_email: email }));
      setIsReturning(false);
      setStep('details');
    } finally {
      setEmailChecking(false);
    }
  };

  // ── Step 2: Details (new participants only) ─────────────────────────────────
  const handleDetailsContinue = (e) => {
    e.preventDefault();
    if (!identity.full_name.trim()) { setDetailsError('Full Name is required.'); return; }
    if (!identity.program.trim()) { setDetailsError('Program is required.'); return; }
    if (!identity.unit_dept_org.trim()) { setDetailsError('Unit / Department / Organization / College is required.'); return; }
    setDetailsError('');
    setStep('documents');
  };

  // ── Document upload ─────────────────────────────────────────────────────────
  const handleFileSelect = (docType, file) => {
    setUploadState(prev => ({ ...prev, [docType]: { file, uploading: false, done: false, error: '' } }));
  };

  const handleUpload = async (docType) => {
    const state = uploadState[docType];
    if (!state?.file) return;
    setUploadState(prev => ({ ...prev, [docType]: { ...prev[docType], uploading: true, error: '' } }));
    const formData = new FormData();
    formData.append('slsu_email', identity.slsu_email);
    formData.append('full_name', identity.full_name || identity.slsu_email);
    formData.append('program', identity.program || '');
    formData.append('unit_dept_org', identity.unit_dept_org || '');
    formData.append('doc_type', docType);
    formData.append('file', state.file);
    try {
      await submitParticipantDocument(token, formData);
      setUploadState(prev => ({ ...prev, [docType]: { file: state.file, uploading: false, done: true, error: '' } }));
    } catch (err) {
      const msg = err.response?.data?.detail || 'Upload failed. Please try again.';
      setUploadState(prev => ({ ...prev, [docType]: { ...prev[docType], uploading: false, error: msg } }));
    }
  };

  const allDone = DOC_TYPES.every(dt => uploadState[dt.key]?.done);

  // ─── Loading / Error ────────────────────────────────────────────────────────
  if (portalLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0d4332] to-[#1a6b50] flex items-center justify-center p-6">
        <div className="text-white text-center">
          <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/80 text-sm font-medium">Loading portal…</p>
        </div>
      </div>
    );
  }

  if (portalError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0d4332] to-[#1a6b50] flex items-center justify-center p-6">
        <div className="bg-white rounded-[24px] shadow-2xl p-10 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Link Unavailable</h2>
          <p className="text-gray-500 text-sm">{portalError}</p>
        </div>
      </div>
    );
  }

  // ─── Success ────────────────────────────────────────────────────────────────
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0d4332] to-[#1a6b50] flex items-center justify-center p-6">
        <div className="bg-white rounded-[24px] shadow-2xl p-10 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">All Done!</h2>
          <p className="text-gray-500 text-sm">Your documents have been submitted to <strong>{portalInfo?.activity_name}</strong>. The Person In Charge will be notified.</p>
        </div>
      </div>
    );
  }

  const stepInfo = STEP_LABELS[step] || STEP_LABELS.email;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0d4332] to-[#1a6b50]">
      {/* Page Header */}
      <div className="py-8 px-6 text-center">
        <div className="flex items-center justify-center space-x-3 mb-3">
          <img src="/assets/osas-logo.png" alt="OSAS" className="w-10 h-10 object-contain" />
          <span className="text-white/80 text-sm font-semibold tracking-wider uppercase">SLSU - OSAS</span>
        </div>
        <h1 className="text-2xl md:text-3xl font-black text-white leading-tight mb-1">
          {portalInfo?.activity_name}
        </h1>
        <p className="text-white/70 text-sm"> Participant Document Submission</p>
        {formatDeadline() && (
          <div className="inline-flex items-center mt-3 bg-white/10 backdrop-blur-sm rounded-md px-4 py-1.5 space-x-2">
            <svg className="w-3.5 h-3.5 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-amber-200 text-xs font-semibold">Deadline: {formatDeadline()}</span>
          </div>
        )}
      </div>

      {/* Card */}
      <div className="max-w-lg mx-auto px-4 pb-16">
        <div className="bg-white rounded-[24px] shadow-2xl overflow-hidden">

          {/* Step header */}
          <div className="border-b border-gray-100 px-6 py-4 bg-gray-50/50 flex items-center justify-between">
            <span className="text-xs font-black tracking-wider text-[#086C53] uppercase">
              {stepInfo.title}
            </span>
            <span className="text-[11px] font-bold text-gray-400">Step {stepInfo.step}</span>
          </div>

          {/* ── Step 1: Email ─────────────────────────────────────────────────── */}
          {step === 'email' && (
            <form onSubmit={handleEmailContinue} className="p-6 space-y-4">
              <div>
                <p className="text-gray-800 font-bold text-[15px] mb-1">Enter Your SLSU Email</p>
                <p className="text-gray-500 text-xs">
                  If you've submitted before, you'll go straight to uploading.
                </p>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  SLSU Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  placeholder="juan@slsu.edu.ph"
                  value={emailInput}
                  onChange={e => setEmailInput(e.target.value)}
                  required
                  autoFocus
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#086C53] focus:ring-1 focus:ring-[#086C53]/30 transition-all"
                />
              </div>
              {emailError && (
                <p className="text-red-500 text-xs font-medium bg-red-50 rounded-lg px-3 py-2">{emailError}</p>
              )}
              <button
                type="submit"
                disabled={emailChecking}
                className="w-full bg-[#086C53] text-white font-bold py-3 rounded-xl text-sm hover:bg-[#065440] transition-colors shadow-sm disabled:opacity-60 flex items-center justify-center space-x-2"
              >
                {emailChecking ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Checking…</span>
                  </>
                ) : <span>Continue →</span>}
              </button>
            </form>
          )}

          {/* ── Step 2: Details (new participants only) ───────────────────────── */}
          {step === 'details' && (
            <form onSubmit={handleDetailsContinue} className="p-6 space-y-4">
              <div>
                <p className="text-gray-800 font-bold text-[15px] mb-1">Your Information</p>
                <p className="text-gray-500 text-xs">Fill in your details.</p>
              </div>

              {/* Email read-only badge */}
              <div className="flex items-center space-x-2 bg-[#086C53]/5 border border-[#086C53]/20 rounded-xl px-4 py-2.5">
                <svg className="w-4 h-4 text-[#086C53] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-sm text-[#086C53] font-semibold truncate">{identity.slsu_email}</span>
                <button
                  type="button"
                  onClick={() => { setStep('email'); setIdentity(prev => ({ ...prev, slsu_email: '' })); }}
                  className="ml-auto text-[10px] text-gray-400 hover:text-gray-600 font-medium underline flex-shrink-0"
                >
                  Change
                </button>
              </div>

              {[
                { id: 'full_name', label: 'Full Name', placeholder: 'Juan Dela Cruz', required: true },
                { id: 'program', label: 'Program', placeholder: 'e.g. BSIT 3A', required: true },
                { id: 'unit_dept_org', label: 'Unit / Department / Organization / College (any applicable)', placeholder: 'e.g. College of Computing', required: true },
              ].map(field => (
                <div key={field.id}>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    {field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}
                  </label>
                  <input
                    type="text"
                    placeholder={field.placeholder}
                    value={identity[field.id]}
                    onChange={e => setIdentity(prev => ({ ...prev, [field.id]: e.target.value }))}
                    required={field.required}
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#086C53] focus:ring-1 focus:ring-[#086C53]/30 transition-all"
                  />
                </div>
              ))}

              {detailsError && (
                <p className="text-red-500 text-xs font-medium bg-red-50 rounded-lg px-3 py-2">{detailsError}</p>
              )}

              <button
                type="submit"
                className="w-full bg-[#086C53] text-white font-bold py-3 rounded-xl text-sm hover:bg-[#065440] transition-colors shadow-sm flex items-center justify-center"
              >
                Next: Upload Documents →
              </button>
            </form>
          )}

          {/* ── Step 3: Documents ────────────────────────────────────────────── */}
          {step === 'documents' && (
            <div className="p-6 space-y-4">
              {/* Identity banner */}
              <div className={`rounded-xl px-4 py-3 flex items-center justify-between ${isReturning ? 'bg-green-50 border border-blue-100' : 'bg-gray-50 border border-gray-100'}`}>
                <div>
                  {isReturning ? (
                    <>
                      <p className="text-[12px] font-bold text-[#0D3B2E]">Welcome back!</p>
                      <p className="text-[11px] text-[#0D3B2E]">{identity.slsu_email}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-[12px] font-bold text-gray-700">{identity.full_name}</p>
                      <p className="text-[11px] text-gray-400">{identity.slsu_email}</p>
                    </>
                  )}
                </div>
                <button
                  onClick={() => setStep('email')}
                  className="text-[10px] text-gray-400 hover:text-gray-600 font-medium underline flex-shrink-0 ml-3"
                >
                  Change email
                </button>
              </div>

              <p className="text-gray-500 text-xs text-center">Upload your requirements now!</p>

              {DOC_TYPES.map(dt => {
                const st = uploadState[dt.key] || {};
                const isDone = st.done;
                const isUploading = st.uploading;
                const hasFile = !!st.file;
                const docLabel = dt.key === 'moa' ? (portalInfo?.moa_label || dt.label) : dt.label;
                const templateUrl = portalInfo?.templates?.[dt.key];

                return (
                  <div
                    key={dt.key}
                    className={`rounded-[16px] border-2 p-4 transition-all ${isDone ? 'border-green-400 bg-green-45' : 'border-gray-200 bg-gray-50'}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-center space-x-2.5">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${isDone ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                          {isDone ? <CheckIcon /> : (
                            <span className="text-[10px] font-black">{dt.code || '—'}</span>
                          )}
                        </div>
                        <div>
                          <p className={`text-[13px] font-bold leading-tight ${isDone ? 'text-green-800' : 'text-gray-800'}`}>
                            {docLabel}
                          </p>
                          {isDone && existingDocs[dt.key] && (
                            <a href={existingDocs[dt.key]} target="_blank" rel="noreferrer"
                              className="text-[10px] text-green-600 font-semibold hover:underline">
                              View submitted file ↗
                            </a>
                          )}
                        </div>
                      </div>
                      {templateUrl && (
                        <a href={templateUrl} target="_blank" rel="noreferrer"
                          className="flex items-center space-x-1 text-[10px] font-bold text-[#086C53] bg-white border border-[#086C53]/30 rounded-lg px-2.5 py-1 hover:bg-[#086C53]/5 transition-colors flex-shrink-0">
                          <DownloadIcon />
                          <span>Template</span>
                        </a>
                      )}
                    </div>

                    {/* File picker */}
                    <div
                      className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all ${hasFile ? 'border-[#086C53]/50 bg-[#086C53]/5' : 'border-gray-300 hover:border-gray-400 bg-white'}`}
                      onClick={() => fileRefs.current[dt.key]?.click()}
                    >
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        className="hidden"
                        ref={el => fileRefs.current[dt.key] = el}
                        onChange={e => { if (e.target.files?.[0]) handleFileSelect(dt.key, e.target.files[0]); }}
                      />
                      {hasFile ? (
                        <p className="text-[12px] font-semibold text-[#086C53] truncate">📎 {st.file.name}</p>
                      ) : (
                        <div className="flex flex-col items-center space-y-1">
                          <UploadIcon />
                          <p className="text-[11px] text-gray-400 font-medium">Click to select file</p>
                        </div>
                      )}
                    </div>

                    {st.error && <p className="text-xs text-red-500 font-medium mt-2">{st.error}</p>}

                    {hasFile && !isDone && (
                      <button
                        onClick={() => handleUpload(dt.key)}
                        disabled={isUploading}
                        className="mt-3 w-full bg-[#086C53] text-white text-xs font-bold py-2.5 rounded-lg hover:bg-[#065440] transition-colors disabled:opacity-60 flex items-center justify-center space-x-2"
                      >
                        {isUploading ? (
                          <>
                            <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            <span>Uploading…</span>
                          </>
                        ) : (
                          <>

                            <span>Submit</span>
                          </>
                        )}
                      </button>
                    )}

                    {isDone && (
                      <button
                        onClick={() => {
                          setUploadState(prev => ({ ...prev, [dt.key]: { file: null, uploading: false, done: false, error: '' } }));
                          if (fileRefs.current[dt.key]) fileRefs.current[dt.key].value = '';
                        }}
                        className="mt-2 text-[10px] text-gray-400 hover:text-gray-600 font-medium underline"
                      >
                        Replace with a new file
                      </button>
                    )}
                  </div>
                );
              })}

              {allDone && (
                <button
                  onClick={() => setStep('success')}
                  className="w-full mt-2 bg-green-600 text-white font-bold py-3.5 rounded-xl text-sm hover:bg-green-700 transition-colors shadow-sm flex items-center justify-center space-x-2"
                >
                  <CheckIcon />
                  <span>All Documents Submitted — Finish</span>
                </button>
              )}
            </div>
          )}
        </div>

        <p className="text-center text-white/40 text-xs mt-6">
          SLSU - OSAS &middot; OrgPass
        </p>
      </div>
    </div>
  );
};

export default ParticipantPortal;
